import AIChatFloatingButton from '@cobliteam/ai/src/containers/AIChatFloating/AIChatFloatingButton';
import { useAIAssistant, useAIAssistantStore } from '@cobliteam/ai/src/store/useAIAssistant';
import { useFloatingChat } from '@cobliteam/ai/src/store/useFloatingChat';
import AIUserInviteModal from '@cobliteam/ai/src/ui/AIUserInviteModal';
import { Analytics } from '@cobliteam/analytics';
import { buildUserInfoChanges, fromE164BR, toE164BR } from '@cobliteam/api-lib';
// Common Types
import { Location, locationName } from '@cobliteam/common-types';
// Developer Tools
// Hooks
import useChatWidget from '@cobliteam/hooks/src/useChatWidget';
import useChatWidgetPositionSync from '@cobliteam/hooks/src/useChatWidget/useChatWidgetPositionSync';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import useIntercom from '@cobliteam/hooks/src/useIntercom';
import useLogout from '@cobliteam/hooks/src/useSession/useLogout';
import useUpdateAccount from '@cobliteam/hooks/src/useSession/useUpdateAccount';
import { lazy } from '@cobliteam/lazy-with-retries';
// Preferences
import Preferences from '@cobliteam/preferences';
// UI Business Components
import AppHeader from '@cobliteam/ui-business/components/AppHeader';
import AppImpersonate from '@cobliteam/ui-business/components/AppImpersonate';
import AppSidebar from '@cobliteam/ui-business/components/AppSidebar';
import { SidebarInset, SidebarProvider } from '@cobliteam/ui-business/components/AppSidebar/components/Sidebar';
import { LazyMotionProvider } from '@cobliteam/ui-business/components/motion/LazyMotionProvider';
// React and Router
import { type ReactNode, Suspense, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { NUDGE_LIMIT, NUDGE_SOURCES } from '../../nudges/nudgeSources';
import AppContentLoadingBoundary from './AppContentLoadingBoundary';

// AIAssistantSidepanel owns the Mastra chat client and its markdown/shiki renderer stack.
// Keep it out of the entry chunk — mount only after the user opens the assistant for the
// first time; the Zustand store keeps its state so subsequent closes/opens are cheap.
const AIAssistantSidepanel = lazy(() => import('@cobliteam/ai/src/containers/AIAssistant/AIAssistantSidepanel'));
// Segue com `motion.*` (layout animation precisa do domMax) e só monta com nudge elegível: fica fora do bundle inicial.
const NudgeFloatingStack = lazy(() => import('@cobliteam/ui-business/components/Nudge/NudgeFloatingStack'));
// O menu das ferramentas (Base UI) sai do chunk inicial (ADR-0022).
const DeveloperTools = lazy(() => import('@cobliteam/developer-tools'));
// Botão do tour guiado por IA: o painel (cliente Mastra) e o react-joyride carregam só depois, dentro dele.
const GuidedTourLauncher = lazy(() => import('@cobliteam/guided-tour/src/components/GuidedTourLauncher'));

import useInitializeThirdParty from '../../hooks/useInitializeThirdParty';
// Local Hooks and Utils
import useRedirectAfterLogin from '../../hooks/useRedirectAfterLogin';

function AISidepanelStateSync({ pathname, hide, setPosition }: { pathname: string; hide: (value: boolean) => void; setPosition: (position: 'default' | 'tab') => void }) {
  const { isAISidepanelOpen } = useAIAssistant();

  useEffect(() => {
    const hasDefaultTab = pathname === Location.MONITORING || pathname === Location.TRACKING || pathname === Location.GEOFENCES_CREATE || pathname.startsWith('/geofences/edit/');

    if (isAISidepanelOpen) {
      hide(true);
    } else {
      hide(false);
      setPosition(hasDefaultTab ? 'tab' : 'default');
    }

    return () => {
      hide(false);
    };
  }, [pathname, setPosition, hide, isAISidepanelOpen]);

  return null;
}

// Fora do V2 fica sobre o botão flutuante e some com ele (sidepanel aberto, páginas /ai); no V2, onde não há
// botão flutuante, entra ao lado dos controles de navegação do mapa.
function GuidedTourLauncherSlot({ showAIChat, isAISidepanelOpen, isAIRoute, pathname }: { showAIChat: boolean; isAISidepanelOpen: boolean; isAIRoute: boolean; pathname: string }) {
  const { aiGuidedTour } = useFeatureFlags();
  if (!(aiGuidedTour && showAIChat) || isAISidepanelOpen || isAIRoute) return null;

  return (
    <Suspense fallback={null}>
      <GuidedTourLauncher placement={pathname === Location.MONITORING_V2 ? 'map' : 'floating'} />
    </Suspense>
  );
}

function AppLayout({ children }: { children: ReactNode }) {
  const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';
  const { mutate: logout } = useLogout();
  const { mutate: updateAccount } = useUpdateAccount();
  const { setPosition, hide } = useChatWidget();
  const setIntercomOpen = useFloatingChat((s) => s.setIntercomOpen);
  const isAISidepanelOpen = useAIAssistantStore((s) => s.isAISidepanelOpen);
  // Track first-ever open so we keep the sidepanel mounted once loaded (preserves chat state).
  const hasEverOpenedAssistantRef = useRef(false);
  if (isAISidepanelOpen) hasEverOpenedAssistantRef.current = true;

  const { ddRumSampleRate, ddRumReplaySampleRate, ddRumProfilingSampleRate, oneSignal, useDdRum, sidebar, aiChat, register, unregister, aiUserModalUpdate } = useFeatureFlags();
  const history = useHistory();
  const { pathname } = useLocation();
  const [isAIUserInviteModalOpen, setIsAIUserInviteModalOpen] = useState(true);
  const currentLocation = locationName[pathname as Location];
  const { getSession, useGetUserData } = useUser();
  const { redirectSessionStorage } = useRedirectAfterLogin();
  const session = getSession();
  const { data: userData } = useGetUserData();
  const currentFleet = session?.fleet;
  const fleets = userData?.userFleets;
  const hasRegisteredPhoneNumber = Boolean(session.user.phoneNumber?.trim());
  // Frota inativa: os elementos flutuantes de IA apontam para APIs bloqueadas na sessão
  // restrita (403), então não montam — mesmo racional dos controles escondidos no AppHeader.
  const isInactiveFleet = session?.fleet.active === false;
  const showAIEntryPoints = !(isDemoMode || isInactiveFleet);
  // Na demo o chat fica: `libs/msw-demo/src/handlers/ai.ts` responde (ou o backend real, no live mode).
  const showAIChat = !isInactiveFleet;
  const isAIRoute = pathname === Location.AI || pathname.startsWith(`${Location.AI}/`);
  // The full-page AI sidebar owns this corner; global nudges there cover its footer actions.
  const showFloatingNudges = showAIEntryPoints && !isAIRoute;
  // No Monitoramento V2 o canto inferior direito é dos controles do mapa; a Cobli IA segue no menu lateral.
  const showAIChatFloatingButton = showAIChat && pathname !== Location.MONITORING_V2;
  // O gate por isInactiveFleet cobre também o sidepanel já aberto numa frota ativa: se a sessão
  // virar inativa sem remount completo, ele desmonta em vez de seguir chamando a API bloqueada.
  const shouldMountAssistant = showAIChat && hasEverOpenedAssistantRef.current;
  const shouldShowAIUserInviteModal = !isDemoMode && aiChat && aiUserModalUpdate && !hasRegisteredPhoneNumber && !isInactiveFleet;
  const isUserInviteModalVisible = Boolean(shouldShowAIUserInviteModal && isAIUserInviteModalOpen);
  const setUserInviteModalOpen = useAIAssistantStore((s) => s.setUserInviteModalOpen);

  // A introdução da /ai é modal como este convite: publicar o estado evita os dois na tela.
  useEffect(() => {
    setUserInviteModalOpen(isUserInviteModalVisible);
  }, [isUserInviteModalVisible, setUserInviteModalOpen]);

  useChatWidgetPositionSync();
  useIntercom({
    enabled: !isDemoMode,
    onClose: () => {
      hide(false);
      setIntercomOpen(false);
    },
  });
  useInitializeThirdParty({
    session,
    userData,
    featureFlags: { ddRumSampleRate, ddRumReplaySampleRate, ddRumProfilingSampleRate, oneSignal, useDdRum, sidebar, aiChat, aiUserModalUpdate },
  });

  const handleBetaOptInChange = async (enabled: boolean) => {
    if (enabled) {
      await register('userEarlyAccess');
    } else {
      await unregister('userEarlyAccess');
    }
  };

  const handleInvitePhoneNumberUpdate = (phoneNumber: string) => {
    const initialPhoneE164 = session.user.phoneNumber ?? '';
    const nextPhoneE164 = toE164BR(phoneNumber);
    const changes = buildUserInfoChanges({
      initialName: session.user.name,
      initialPhone: initialPhoneE164,
      nextName: session.user.name,
      nextPhone: nextPhoneE164,
    });

    if (!('phone_number' in changes)) return;

    updateAccount({ changes });
  };

  // Initialize core services
  useEffect(() => {
    Analytics.initialize();
    Preferences.initialize();
  }, []);

  // Handle redirect after login
  useEffect(() => {
    const redirectUrl = redirectSessionStorage();
    if (redirectUrl) {
      history.push(redirectUrl);
    }
  }, [history, redirectSessionStorage]);

  // Track page views
  useEffect(() => {
    if (!session) return;
    Analytics.pageview(currentLocation, { fleetId: session.fleet.id });
  }, [currentLocation, session]);

  return (
    <AppImpersonate>
      <LazyMotionProvider>
        <SidebarProvider>
          <AppSidebar fleets={fleets || []} currentFleet={currentFleet} floatingSecondary={isAIRoute} />
          <SidebarInset>
            <AppHeader user={session.user} logout={() => logout()} />
            <div className='flex h-screen-without-header overflow-hidden'>
              <section id='app-body' data-testid='app-body' className='relative flex-1 overflow-y-auto bg-shell text-foreground'>
                <AppContentLoadingBoundary>{children}</AppContentLoadingBoundary>
                <Suspense fallback={null}>
                  <DeveloperTools />
                </Suspense>
                {showFloatingNudges && (
                  <Suspense fallback={null}>
                    <NudgeFloatingStack sources={NUDGE_SOURCES} limit={NUDGE_LIMIT} />
                  </Suspense>
                )}
                {showAIChatFloatingButton && <AIChatFloatingButton />}
                <GuidedTourLauncherSlot showAIChat={showAIChat} isAISidepanelOpen={isAISidepanelOpen} isAIRoute={isAIRoute} pathname={pathname} />
                {shouldShowAIUserInviteModal && (
                  <AIUserInviteModal
                    fleetId={session.fleet.id}
                    userId={session.user.id}
                    initialPhoneNumber={fromE164BR(session.user.phoneNumber ?? '')}
                    open={isAIUserInviteModalOpen}
                    onOpenChange={setIsAIUserInviteModalOpen}
                    onBetaOptInChange={handleBetaOptInChange}
                    onPhoneNumberUpdate={handleInvitePhoneNumberUpdate}
                  />
                )}
              </section>
              {showAIChat && <AISidepanelStateSync pathname={pathname} hide={hide} setPosition={setPosition} />}
              {shouldMountAssistant && (
                <Suspense fallback={null}>
                  <AIAssistantSidepanel />
                </Suspense>
              )}
            </div>
          </SidebarInset>
        </SidebarProvider>
      </LazyMotionProvider>
    </AppImpersonate>
  );
}

export default AppLayout;
